import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Modal, Icon } from 'semantic';
import { Table, Checkbox, Loader } from 'semantic-ui-react';
import { groupBy, size } from 'lodash-es';

import { request } from 'utils/api';
import Pagination from 'components/Pagination';
import WrappedModal from 'components/modal/WrappedModal';
import ActionButton from 'components/semantic/ActionButton';
import {
  ConnectorSelectionValue,
  ConnectorSelectionValueWithMetadata,
  ConnectorsSelection,
  EVSE_CONTROLLER_LIMIT,
  evseControllersToConnectorSelectionValues,
  getConnectorSelectionKey,
  powerTypeToFriendlyName,
  standardToFriendlyName,
} from './utils';
import ErrorMessage from 'components/ErrorMessage';
import { EVSE_CONTROLLERS_V1_FAST_SEARCH_API_URL } from 'subdomains/cpo/router/api';
import { EvseController } from 'types/evse-controller';
import { useQuery } from '@tanstack/react-query';

type Props = {
  trigger: React.ReactNode;
  onSubmit: (connectorsSelection: ConnectorsSelection) => void;
  /**
   * Connectors selection determines which connectors are selected for on the form
   * The reason this is a map is because we will be possibily dealing with a lot of data
   * So, to avoid doing loops over and over again, we will store the connectors selection in a map
   */
  connectorsSelection: ConnectorsSelection;
};

export function ChargingGroupReviewModal({
  trigger,
  onSubmit,
  connectorsSelection,
}: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [evseControllersPage, setEvseControllersPage] = useState(1);
  const [collapsedStations, setCollapsedStations] = useState<Set<string>>(
    new Set()
  );

  const [connectorsSelectionCache, setConnectorsSelectionCache] =
    useState(connectorsSelection);

  const [allCollapsed, setAllCollapsed] = useState(true);

  useEffect(() => {
    setConnectorsSelectionCache(connectorsSelection);
  }, [connectorsSelection]);

  const evseControllersSearchQuery = useMemo(() => {
    const opChargingStationIds = Object.values(connectorsSelection).map(
      (connector) => connector.opChargingStationId
    );
    return {
      opChargingStationIds,
      skip: (evseControllersPage - 1) * EVSE_CONTROLLER_LIMIT,
      limit: EVSE_CONTROLLER_LIMIT,
      sort: {
        field: 'createdAt',
        order: 'desc',
      },
    };
  }, [connectorsSelection, evseControllersPage]);

  const {
    data: response,
    isPending: loadingFetchingEvseControllers,
    error: errorFetchingEvseControllers,
  } = useQuery<{
    data: EvseController[];
    meta: { total: number; limit: number };
  }>({
    queryKey: [
      EVSE_CONTROLLERS_V1_FAST_SEARCH_API_URL,
      evseControllersSearchQuery,
    ],
    queryFn: async () => {
      return await request({
        path: EVSE_CONTROLLERS_V1_FAST_SEARCH_API_URL,
        method: 'POST',
        body: evseControllersSearchQuery,
      });
    },
  });

  const evseControllers = response?.data ?? [];
  const evseControllersMeta = response?.meta ?? { total: 0, limit: 0 };

  const connectors: ConnectorSelectionValueWithMetadata[] = useMemo(
    () => evseControllersToConnectorSelectionValues(evseControllers),
    [evseControllers]
  );

  const connectorsGroupedByOpChargingStationId = useMemo(
    () => groupBy(connectors, 'opChargingStationId'),
    [connectors]
  );

  const addConnectorToSelection = useCallback(
    (connector: ConnectorSelectionValue) => {
      setConnectorsSelectionCache({
        ...connectorsSelectionCache,
        [getConnectorSelectionKey(connector)]: connector,
      });
    },
    [connectorsSelectionCache]
  );

  const addConnectorsToSelection = useCallback(
    (connectors: ConnectorSelectionValue[]) => {
      const newConnectorsSelection = { ...connectorsSelectionCache };
      connectors.forEach((connector) => {
        newConnectorsSelection[getConnectorSelectionKey(connector)] = connector;
      });
      setConnectorsSelectionCache(newConnectorsSelection);
    },
    [connectorsSelectionCache]
  );

  const removeConnectorFromSelection = useCallback(
    (connector: ConnectorSelectionValue) => {
      const connectorSelectionKey = getConnectorSelectionKey(connector);

      const newConnectorsSelection = { ...connectorsSelectionCache };
      delete newConnectorsSelection[connectorSelectionKey];

      setConnectorsSelectionCache(newConnectorsSelection);
    },
    [connectorsSelectionCache]
  );

  const removeConnectorsFromSelection = useCallback(
    (connectors: ConnectorSelectionValue[]) => {
      const newConnectorsSelection = { ...connectorsSelectionCache };
      connectors.forEach((connector) => {
        delete newConnectorsSelection[getConnectorSelectionKey(connector)];
      });
      setConnectorsSelectionCache(newConnectorsSelection);
    },
    [connectorsSelectionCache]
  );

  const handleSelectAllInPage = useCallback(() => {
    const newConnectorsSelection = { ...connectorsSelectionCache };
    connectors.forEach((connector) => {
      newConnectorsSelection[getConnectorSelectionKey(connector)] = connector;
    });

    setConnectorsSelectionCache(newConnectorsSelection);
  }, [connectorsSelectionCache, connectors]);

  const handleClearAllInPage = useCallback(() => {
    const newConnectorsSelection = { ...connectorsSelectionCache };
    connectors.forEach((connector) => {
      delete newConnectorsSelection[getConnectorSelectionKey(connector)];
    });

    setConnectorsSelectionCache(newConnectorsSelection);
  }, [connectorsSelectionCache, connectors]);

  const handleClearAll = useCallback(() => {
    setConnectorsSelectionCache({});
  }, []);

  const submit = useCallback(() => {
    setIsOpen(false);
    setEvseControllersPage(1);
    onSubmit(connectorsSelectionCache);
    setAllCollapsed(true);
    setCollapsedStations(new Set());
  }, [connectorsSelectionCache, onSubmit]);

  const cancel = useCallback(() => {
    setIsOpen(false);
    setEvseControllersPage(1);
    setConnectorsSelectionCache(connectorsSelection);
    setAllCollapsed(true);
    setCollapsedStations(new Set());
  }, [connectorsSelection]);

  const totalSelected = size(connectorsSelectionCache);

  const allConnectorsSelectedInPage = connectors.every(
    (connector) => connectorsSelectionCache[getConnectorSelectionKey(connector)]
  );
  const someConnectorsSelectedInPage = connectors.some(
    (connector) => connectorsSelectionCache[getConnectorSelectionKey(connector)]
  );

  const toggleStationCollapse = useCallback((opChargingStationId: string) => {
    setCollapsedStations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(opChargingStationId)) {
        newSet.delete(opChargingStationId);
      } else {
        newSet.add(opChargingStationId);
      }
      return newSet;
    });
  }, []);

  const isStationCollapsed = useCallback(
    (opChargingStationId: string) => {
      return !collapsedStations.has(opChargingStationId);
    },
    [collapsedStations]
  );

  const toggleAllStationsCollapse = useCallback(() => {
    const allStationIds = Object.keys(connectorsGroupedByOpChargingStationId);
    if (allCollapsed) {
      setCollapsedStations(new Set(allStationIds));
    } else {
      setCollapsedStations(new Set());
    }
    setAllCollapsed(!allCollapsed);
  }, [allCollapsed, connectorsGroupedByOpChargingStationId]);

  const handlePageChange = useCallback((page: number) => {
    setEvseControllersPage(page);
    setAllCollapsed(true);
    setCollapsedStations(new Set());
  }, []);

  return (
    <WrappedModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      trigger={trigger}
      onClose={cancel}>
      <Modal.Header>
        {t(
          'providerAdmin.chargingGroupReviewModal.reviewSelectionTitle',
          'Review Selection'
        )}
      </Modal.Header>
      <Modal.Content>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}>
          <div>
            <strong>
              {t(
                'providerAdmin.chargingGroupReviewModal.itemsSelected',
                '{{count}} items selected',
                {
                  count: totalSelected,
                }
              )}
            </strong>
          </div>
          <div>
            {size(connectorsSelection) > 0 && (
              <ActionButton
                primary
                size="tiny"
                style={{ paddingLeft: 5 }}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  handleClearAll();
                }}>
                {t(
                  'providerAdmin.chargingGroupReviewModal.removeAllButtonText',
                  'Remove all'
                )}
              </ActionButton>
            )}
          </div>
        </div>

        <div>
          {loadingFetchingEvseControllers && <Loader active />}
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {size(connectors) > 0 && (
                    <Icon
                      name={allCollapsed ? 'angle-right' : 'angle-down'}
                      style={{ cursor: 'pointer' }}
                      onClick={toggleAllStationsCollapse}
                    />
                  )}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {size(connectors) > 0 && (
                    <Checkbox
                      checked={allConnectorsSelectedInPage}
                      indeterminate={
                        someConnectorsSelectedInPage &&
                        !allConnectorsSelectedInPage
                      }
                      onChange={(
                        e: React.FormEvent<HTMLInputElement>,
                        { checked }: { checked?: boolean }
                      ) => {
                        if (checked) {
                          handleSelectAllInPage();
                        } else {
                          handleClearAllInPage();
                        }
                      }}
                    />
                  )}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t(
                    'providerAdmin.chargingGroupReviewModal.evseId',
                    'EVSE ID'
                  )}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t(
                    'providerAdmin.chargingGroupReviewModal.ocppId',
                    'OCPP ID'
                  )}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t(
                    'providerAdmin.chargingGroupReviewModal.account',
                    'Account'
                  )}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('providerAdmin.chargingGroupReviewModal.info', 'Info')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {size(connectors) === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={6} style={{ textAlign: 'center' }}>
                    {loadingFetchingEvseControllers
                      ? t(
                          'providerAdmin.chargingGroupReviewModal.loadingChargingStations',
                          'Loading charging stations...'
                        )
                      : t(
                          'providerAdmin.chargingGroupReviewModal.noChargingStationsFound',
                          'No charging stations found'
                        )}
                  </Table.Cell>
                </Table.Row>
              )}
              {Object.entries(connectorsGroupedByOpChargingStationId).map(
                ([opChargingStationId, stationConnectors]) => {
                  const allConnectorsInStationSelected =
                    stationConnectors.every(
                      (connector) =>
                        connectorsSelectionCache[
                          getConnectorSelectionKey(connector)
                        ]
                    );
                  const someConnectorsInStationSelected =
                    stationConnectors.some(
                      (connector) =>
                        connectorsSelectionCache[
                          getConnectorSelectionKey(connector)
                        ]
                    );

                  return (
                    <React.Fragment key={opChargingStationId}>
                      <Table.Row style={{ backgroundColor: '#f9f9f9' }}>
                        <Table.Cell style={{ paddingRight: 0 }}>
                          <Icon
                            name={
                              isStationCollapsed(opChargingStationId)
                                ? 'angle-right'
                                : 'angle-down'
                            }
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                              toggleStationCollapse(opChargingStationId)
                            }
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            checked={allConnectorsInStationSelected}
                            indeterminate={
                              someConnectorsInStationSelected &&
                              !allConnectorsInStationSelected
                            }
                            onChange={(
                              e: React.FormEvent<HTMLInputElement>,
                              { checked }: { checked?: boolean }
                            ) => {
                              if (checked) {
                                addConnectorsToSelection(stationConnectors);
                              } else {
                                removeConnectorsFromSelection(
                                  stationConnectors
                                );
                              }
                            }}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          {stationConnectors[0]?.evseController.evseId || '-'}
                        </Table.Cell>
                        <Table.Cell>
                          {stationConnectors[0]?.evseController.ocppIdentity ||
                            '-'}
                        </Table.Cell>
                        <Table.Cell>
                          {stationConnectors[0]?.evseController.account?.name ||
                            '-'}
                        </Table.Cell>
                        <Table.Cell />
                      </Table.Row>
                      {/* Connectors for this station */}
                      {!isStationCollapsed(opChargingStationId) &&
                        stationConnectors.map((connector) => {
                          const selected =
                            !!connectorsSelectionCache[
                              getConnectorSelectionKey(connector)
                            ];

                          return (
                            <Table.Row
                              key={getConnectorSelectionKey(connector)}
                              style={{ paddingLeft: 20 }}>
                              <Table.Cell />
                              <Table.Cell>
                                <Checkbox
                                  checked={selected}
                                  onChange={(
                                    e: React.FormEvent<HTMLInputElement>,
                                    { checked }: { checked?: boolean }
                                  ) => {
                                    if (checked) {
                                      addConnectorToSelection(connector);
                                    } else {
                                      removeConnectorFromSelection(connector);
                                    }
                                  }}
                                />
                              </Table.Cell>
                              <Table.Cell>{connector.evseId || '-'}</Table.Cell>
                              <Table.Cell />
                              <Table.Cell />
                              <Table.Cell>
                                {connector.powerType && connector.standard ? (
                                  <>
                                    {powerTypeToFriendlyName(
                                      connector.powerType
                                    )}{' '}
                                    (
                                    {standardToFriendlyName(connector.standard)}
                                    )
                                  </>
                                ) : (
                                  '-'
                                )}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                    </React.Fragment>
                  );
                }
              )}
            </Table.Body>
          </Table>
          <ErrorMessage error={errorFetchingEvseControllers} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              className=""
              estimated=""
              limit={EVSE_CONTROLLER_LIMIT}
              page={evseControllersPage}
              total={evseControllersMeta?.total ?? 0}
              onPageChange={(
                e: React.ChangeEvent<HTMLInputElement>,
                { activePage }: { activePage: number }
              ) => {
                handlePageChange(activePage);
              }}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button
          content={t(
            'providerAdmin.chargingGroupReviewModal.closeButtonText',
            'Close'
          )}
          as="button"
          basic
          onClick={cancel}
        />
        {size(connectorsSelection) > 0 && (
          <Button
            content={t(
              'providerAdmin.chargingGroupReviewModal.createButtonText',
              'Update Selection'
            )}
            as="button"
            style={{ marginLeft: '0' }}
            onClick={submit}
          />
        )}
      </Modal.Actions>
    </WrappedModal>
  );
}
